import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SelectOption } from '@kula-ai/components';
import { http } from '@/lib/http';

export const usersWithScopeQueryKey = (scope: string) =>
  ['users', 'v1', 'scope', scope] as const;

/** Row shape from `GET /api/v1/users?scope=…`. */
export type ScopedUser = {
  id: number;
  email?: string | null;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  avatar?: string | null;
  name?: string | null;
};

/**
 * Fetches users from `GET /api/v1/users` filtered by `scope`.
 * Matches the same extraction pattern used by `useGetAllUsers` in the codebase.
 */
async function fetchUsersWithScope(scope: string): Promise<ScopedUser[]> {
  const res = await http.get('users', { params: { scope } });
  // The http interceptor returns `response.data` (the JSON body).
  // The body is `{ data: [...users...] }` — extract the inner `data` array.
  if (
    res &&
    typeof res === 'object' &&
    'data' in res &&
    Array.isArray(res.data)
  ) {
    return res.data as ScopedUser[];
  }
  if (Array.isArray(res)) {
    return res as ScopedUser[];
  }
  return [];
}

export function usersToSelectOptions(users: ScopedUser[]): SelectOption[] {
  return users.map((u) => {
    const name = u.full_name?.trim() ?? u.name;
    const label = name || u.email || `User ${u.id}`;
    return {
      value: String(u.id),
      label,
      // Show the email as the secondary line ONLY when the label is the
      // user's name. When the user has no name the label already IS the
      // email, so a description would just duplicate it (the "email in
      // both label and description" case).
      description: name && u.email ? u.email : undefined,
      image: u.avatar_url ?? u.avatar ?? undefined,
      data: { user: u },
    };
  });
}

export type UsersWithScopeFormat = 'selectOptions' | 'none';

export type UseUsersWithScopeOptions = {
  format?: UsersWithScopeFormat;
};

/**
 * `GET /api/v1/users` with a dynamic `scope` query param.
 * Returns Kula `SelectOption[]` by default, or raw `ScopedUser[]` with `format: 'none'`.
 */
export function useUsersWithScope(
  _scope: string,
  _options?: { format?: 'selectOptions' }
): UseQueryResult<SelectOption[], Error>;
export function useUsersWithScope(
  _scope: string,
  _options: { format: 'none' }
): UseQueryResult<ScopedUser[], Error>;
export function useUsersWithScope(
  scope: string,
  options?: UseUsersWithScopeOptions
): UseQueryResult<SelectOption[] | ScopedUser[], Error> {
  const format = options?.format ?? 'selectOptions';

  return useQuery({
    queryKey: [...usersWithScopeQueryKey(scope), format],
    queryFn: () => fetchUsersWithScope(scope),
    staleTime: 60_000,
    select:
      format === 'none' ? undefined : (users) => usersToSelectOptions(users),
  }) as UseQueryResult<SelectOption[] | ScopedUser[], Error>;
}
